import React, { useState, useEffect, useCallback, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FiUsers, FiCalendar, FiMapPin, FiLogOut, FiShare2, FiCopy, FiCheck } from "react-icons/fi";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import api from "../api";
import { useAuth } from "../AuthContext";
import SocialFeed from "../components/SocialFeed";
import SocialUploadModal from "../components/SocialUploadModal";
import LoginPromptModal from "../components/LoginPromptModal";
import FollowersModal from "../components/FollowersModal";
import "./SocialEventsPage.css";
import { socialNotificationApi } from "../services/socialNotificationApi";

const LS_KEY = "social_events_selected";

export default function SocialEventsPage() {
  const { user, authenticated, joinedEvents, refreshJoinedEvents } = useAuth();

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unfollowing, setUnfollowing] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [newPhoto, setNewPhoto] = useState(null);
  const [followerCount, setFollowerCount] = useState(null);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef(null);

  // Restore last selected from localStorage
  useEffect(() => {
    if (joinedEvents.length === 0) return;
    const saved = localStorage.getItem(LS_KEY);
    if (saved) {
      const found = joinedEvents.find((e) => String(e.event_id) === saved);
      if (found) { setSelectedEvent(found); return; }
    }
    setSelectedEvent(joinedEvents[0]);
  }, [joinedEvents]);

  // Fetch follower count whenever selected event changes
  useEffect(() => {
    if (!selectedEvent) { setFollowerCount(null); return; }
    api.get(`/followers/count?eid=${selectedEvent.event_id}`)
      .then((r) => setFollowerCount(r.data.count ?? null))
      .catch(() => setFollowerCount(null));
  }, [selectedEvent?.event_id]);

  // Close share sheet on outside click
  useEffect(() => {
    if (!showShare) return;
    function handleOutside(e) {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShowShare(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showShare]);

  // Reset share sheet when selected event changes
  useEffect(() => { setShowShare(false); setCopied(false); }, [selectedEvent?.event_id]);

  // Mark notifications read when user views an event's feed
  useEffect(() => {
    if (!selectedEvent || !authenticated) return;
    socialNotificationApi.markRead(selectedEvent.event_id).catch(() => {});
  }, [selectedEvent?.event_id, authenticated]);

  const socialUrl = selectedEvent
    ? `${window.location.origin}/event/${selectedEvent.id_name}/social`
    : "";
  const qrUrl = selectedEvent
    ? `${process.env.REACT_APP_MODEL_ENDPOINT}/qr?eid=${selectedEvent.event_id}`
    : "";

  function handleCopy() {
    navigator.clipboard.writeText(socialUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleSelectEvent(ev) {
    setSelectedEvent(ev);
    setSidebarOpen(false);
    localStorage.setItem(LS_KEY, String(ev.event_id));
  }

  async function handleUnfollow(ev) {
    if (!window.confirm(`Unfollow "${ev.name}"?`)) return;
    setUnfollowing(true);
    try {
      await api.delete("/followers/unfollow", { data: { event_id: ev.event_id } });
      await refreshJoinedEvents();
      setSelectedEvent(null);
      localStorage.removeItem(LS_KEY);
    } catch {}
    setUnfollowing(false);
  }

  function formatDate(d) {
    if (!d) return null;
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  return (
    <Container fluid className="sep-page mt-3">

      {/* Mobile toggle */}
      <div
        className="d-md-none sep-mobile-bar"
        onClick={() => setSidebarOpen((o) => !o)}
      >
        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#111", letterSpacing: "0.01em" }}>
          {sidebarOpen ? "✕  Close" : "☰ Social Events"}
        </span>
        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#111", letterSpacing: "0.01em" }}>
          {selectedEvent ? selectedEvent.name : ""}
        </span>
      </div>

      <Row>
        {/* Sidebar */}
        <Col md={3} className={`border-end ${sidebarOpen ? "d-block" : "d-none d-md-block"}`}>
          <div className="sep-sidebar">
            <p className="sep-sidebar__label">
              <FiUsers size={13} className="me-1" />
              Followed Events
            </p>

            {joinedEvents.length === 0 ? (
              <p className="sep-sidebar__empty">
                Visit a event wall to auto-follow an event.
              </p>
            ) : (
              joinedEvents.map((ev) => (
                <div
                  key={ev.event_id}
                  className={`sep-sidebar__item ${selectedEvent?.event_id === ev.event_id ? "active" : ""}`}
                  onClick={() => handleSelectEvent(ev)}
                >
                  <p className="sep-sidebar__item-name">{ev.name}</p>
                  {ev.date && (
                    <p className="sep-sidebar__item-meta">
                      <FiCalendar size={11} className="me-1" />
                      {formatDate(ev.date)}
                    </p>
                  )}
                  {ev.city && (
                    <p className="sep-sidebar__item-meta">
                      <FiMapPin size={11} className="me-1" />
                      {ev.city}{ev.country ? `, ${ev.country}` : ""}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </Col>

        {/* Right panel */}
        <Col md={9} className={sidebarOpen ? "d-none d-md-block" : ""}>
          {selectedEvent ? (
            <div className="sep-panel">
              {/* Event header */}
              <div className="sep-panel__header">
                <div>
                  <h4 className="sep-panel__title">{selectedEvent.name}</h4>
                  <div className="sep-panel__meta">
                    {selectedEvent.date && (
                      <span><FiCalendar size={12} className="me-1" />{formatDate(selectedEvent.date)}</span>
                    )}
                    {selectedEvent.city && (
                      <span><FiMapPin size={12} className="me-1" />{selectedEvent.city}</span>
                    )}
                    {followerCount !== null && (
                      <button className="sep-followers-btn" onClick={() => setShowFollowers(true)}>
                        <FiUsers size={12} className="me-1" />{followerCount} {followerCount === 1 ? "follower" : "followers"}
                      </button>
                    )}
                  </div>
                </div>
                <button
                  className="sep-unfollow-btn"
                  onClick={() => handleUnfollow(selectedEvent)}
                  disabled={unfollowing}
                >
                  <FiLogOut size={14} className="me-1" />
                  {unfollowing ? "Leaving…" : "Unfollow"}
                </button>
              </div>

              {/* Social feed inline */}
              <div className="sep-feed-wrapper">
                <div className="sep-feed-cta">
                  {/* Share button */}
                  <div className="social-share-wrapper" ref={shareRef}>
                    <button
                      className="social-page-header__share-btn"
                      onClick={() => setShowShare((v) => !v)}
                      aria-label="Share"
                      title="Share this gallery"
                    >
                      <FiShare2 size={17} />
                    </button>

                    {showShare && (
                      <div className="social-share-sheet">
                        <img src={qrUrl} alt="QR code" className="social-share-sheet__qr" />
                        <p className="social-share-sheet__url">{socialUrl}</p>
                        <div className="social-share-sheet__btns">
                          <button className="social-share-sheet__btn" onClick={handleCopy}>
                            {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                            {copied ? "Copied!" : "Copy link"}
                          </button>
                          <a
                            className="social-share-sheet__btn social-share-sheet__btn--wa"
                            href={`https://wa.me/?text=${encodeURIComponent(`📸 Share your photos! ${socialUrl}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FontAwesomeIcon icon={faWhatsapp} size="sm" />
                            WhatsApp
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    className="social-page-header__cta"
                    onClick={() => authenticated ? setShowUpload(true) : setShowLoginPrompt(true)}
                  >
                    Add a Photo
                  </button>
                </div>
                <SocialFeed
                  key={selectedEvent.event_id}
                  eventId={selectedEvent.event_id}
                  authenticated={authenticated}
                  currentUserId={user?.user_id ?? null}
                  onLoginRequired={() => setShowLoginPrompt(true)}
                  newPhoto={newPhoto}
                />
              </div>
            </div>
          ) : (
            <div className="sep-empty">
              <p>Select an event from the sidebar to see its event wall.</p>
            </div>
          )}
        </Col>
      </Row>

      <SocialUploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        eventId={selectedEvent?.event_id}
        onUploaded={(photo) => { setNewPhoto(photo); setShowUpload(false); }}
      />
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
      />
      <FollowersModal
        isOpen={showFollowers}
        onClose={() => setShowFollowers(false)}
        eventId={selectedEvent?.event_id}
        eventName={selectedEvent?.name}
      />
    </Container>
  );
}
